import React, { MouseEventHandler, ReactElement, useEffect } from "react";
import { useSelector } from "react-redux";

import { useProductServiceApi } from "@api/hooks/productService.hook";
import {
    ProductTileMapper,
    ProductsMapper,
} from "@api/mappers/products.mapper";

import { getComparePageUrl } from "@utils/configuration/aem";
import { ProductFieldSet, StorageKeys } from "@utils/enums";
import { useIsClientSide } from "@utils/hooks/isClient.hook";
import { useScrollLock } from "@utils/hooks/useScrollLock";

import FeatureToggles, {
    FeatureToggleKeys,
} from "@services/featureToggles.services";

import { useThunkDispatch } from "@redux/dispatcher";
import { set as setProductComparisonSkuList } from "@redux/modules/value/productComparisonSkuList/productComparisonSkuList.module";
import { getProductComparisonSkuList } from "@redux/modules/value/productComparisonSkuList/productComparisonSkuList.selectors";
import { getProductDetail } from "@redux/modules/value/productDetail/productDetail.selectors";

import {
    PRODUCT_COMPARISON_BAR_SCROLL_LOCK,
    ProductComparisonBar,
} from "./productComparisonBar.component";
import "./productComparisonBar.scss";

const ProductComparisonBarContainer = (): ReactElement => {
    const productComparisonSkuList = useSelector(getProductComparisonSkuList);
    const productDetail = useSelector(getProductDetail);
    const dispatch = useThunkDispatch();
    const isClient = useIsClientSide();
    const { unlock } = useScrollLock(PRODUCT_COMPARISON_BAR_SCROLL_LOCK);

    const [{ data }, getProducts] = useProductServiceApi(
        {
            url: `/products`,
            method: "GET",
            mapper: ProductsMapper(ProductTileMapper),
        },
        { manual: true, autoCancel: false },
    );

    useEffect(() => {
        if (!localStorage.getItem(StorageKeys.ProductComparisonSkuList)) {
            return;
        }

        let skuList = [];
        try {
            skuList = JSON.parse(
                localStorage.getItem(StorageKeys.ProductComparisonSkuList) ||
                    "[]",
            );
        } catch (e) {}

        dispatch(setProductComparisonSkuList(skuList));
    }, []);

    useEffect(() => {
        localStorage.setItem(
            StorageKeys.ProductComparisonSkuList,
            JSON.stringify(productComparisonSkuList || []),
        );

        if (!productComparisonSkuList?.length) {
            unlock();
            return;
        }

        getProducts({
            params: {
                sku: productComparisonSkuList.join(","),
                fields: ProductFieldSet.ProductTile,
            },
        });
    }, [productComparisonSkuList]);

    const handleUpdateSkuList = (sku: string): void => {
        dispatch(
            setProductComparisonSkuList(
                productComparisonSkuList?.filter(
                    (pcSku: string) => pcSku !== sku,
                ) || [],
            ),
        );
    };

    const handleEmptySkuList: MouseEventHandler<HTMLButtonElement> = (
        event,
    ): void => {
        event.preventDefault();
        dispatch(setProductComparisonSkuList([]));
    };

    if (
        !isClient ||
        !data?.length ||
        !(productComparisonSkuList || []).length ||
        !FeatureToggles.isEnabled(FeatureToggleKeys.PRODUCT_COMPARISON)
    ) {
        return <></>;
    }

    return (
        <ProductComparisonBar
            products={data || []}
            onRemoveProduct={handleUpdateSkuList}
            onEmptySkuList={handleEmptySkuList}
            stickyOrderButton={productDetail.stickyOrderButton}
            comparePagePath={getComparePageUrl()}
        />
    );
};

export { ProductComparisonBarContainer };
